<script setup lang="ts">
import { onUnmounted, ref, computed, watchEffect, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useDashboards, type OmniFilterChangedPayload } from '@/stores/dashboards'
import { handleError } from '@/lib/error'
import { useColorMode } from '@vueuse/core'
import { usePostHog } from '@/lib/posthog'

const route = useRoute()
const dashboards = useDashboards()
const colorMode = useColorMode()
const IFRAME_ORIGIN = 'https://omni.analytics.ginjer.ai'
const { posthog } = usePostHog()

// Get dashboard ID from URL params
const dashboardId = computed(() => route.params.id as string)

// Get dashboard data from store
const dashboard = computed(() => {
  if (!dashboardId.value) return null
  return dashboards.getDashboard(dashboardId.value)
})

const dashboardIframe = ref<string | null>(null)
const dashboardIframeReady = ref(false)
const dashboardIframeStatus = ref<string | null>(null)
const OMNI_STATUS_DONE = 'done'

const stopWatchingDashboardId = watch(dashboardId, () => {
  dashboardIframe.value = null
  dashboardIframeReady.value = false
  dashboardIframeStatus.value = null
})

const stopWatching = watchEffect(async () => {
  if (!dashboardId.value) return null

  dashboardIframe.value = null
  try {
    dashboardIframe.value = await dashboards.getDashboardIframe(dashboardId.value, colorMode.value)
  } catch (error) {
    handleError(error, 'Failed to load dashboard')
  }
})

const iframeMessageListener = (event: MessageEvent) => {
  if (event.origin !== IFRAME_ORIGIN) return

  // this is to track the initialisation of the iframe;
  // if we don't do that, we get the dashboard:filters event as part of the initialisation
  if (dashboardIframeStatus.value !== OMNI_STATUS_DONE && event.data.name === 'status') {
    dashboardIframeStatus.value = event.data.payload.status
  }

  // ensuring the initial status is done means that each 'dashboard:filter-changed'
  // is triggered by an user action
  if (
    event.data.name === 'dashboard:filter-changed' &&
    dashboardIframeStatus.value === OMNI_STATUS_DONE
  ) {
    // update user filters in local storage
    dashboards.updateUserFilters(event.data.payload as OmniFilterChangedPayload)

    // track filter changes in PostHog
    for (const [filterField, { next }] of Object.entries(
      event.data.payload as OmniFilterChangedPayload,
    )) {
      posthog.capture('Dashboard filter changed', {
        filterField,
        filterLabel: next.filter.label,
        value: decodeURIComponent(next.asJsonUrlSearchParam),
      })
    }
  }
}

onMounted(() => {
  window.addEventListener('message', iframeMessageListener)
})

onUnmounted(() => {
  stopWatching()
  stopWatchingDashboardId()
  window.removeEventListener('message', iframeMessageListener)
})

function onIframeContentLoaded() {
  dashboardIframeReady.value = true
}
</script>

<template>
  <AppComponent :id="dashboardId" :title="dashboard ? dashboard.title : 'Missing dashboard'">
    <div
      v-if="dashboardIframe"
      :class="{ 'h-full': dashboardIframeReady, hidden: !dashboardIframeReady }"
    >
      <iframe :src="dashboardIframe" allowfullscreen v-on:load="onIframeContentLoaded"></iframe>
    </div>
    <div
      v-if="!dashboardIframeReady"
      class="flex flex-row gap-2 align-items-end justify-center m-20"
    >
      <USkeleton class="h-[250px] w-4 self-end" />
      <USkeleton class="h-[50px] w-4 self-end" />
      <USkeleton class="h-[200px] w-4 self-end" />
      <USkeleton class="h-[150px] w-4 self-end" />
      <USkeleton class="h-[50px] w-4 self-end" />
      <USkeleton class="h-[200px] w-4 self-end" />
      <USkeleton class="h-[200px] w-4 self-end" />
      <USkeleton class="h-[150px] w-4 self-end" />
      <USkeleton class="h-[50px] w-4 self-end" />
      <USkeleton class="h-[250px] w-4 self-end" />
      <USkeleton class="h-[50px] w-4 self-end" />
      <USkeleton class="h-[200px] w-4 self-end" />
      <USkeleton class="h-[200px] w-4 self-end" />
      <USkeleton class="h-[150px] w-4 self-end" />
    </div>
  </AppComponent>
</template>

<style scoped>
iframe {
  width: 100%;
  height: 100%;
  border: none;
  overflow: hidden;
  display: auto;
  box-sizing: border-box;
}
</style>
